import { z } from 'zod';

// src/enums.ts
var USER_ROLES = ["consumer", "companion", "admin", "moderator"];
var KYC_STATUSES = ["not_started", "pending", "verified", "rejected"];
var COMPANION_STATUSES = [
  "draft",
  "pending_review",
  "approved",
  "suspended",
  "rejected",
  "offboarded"
];
var SESSION_TYPES = ["chat", "audio", "video"];
var SESSION_STATUSES = [
  "requested",
  "authorizing",
  "ongoing",
  "settling",
  "completed",
  "cancelled",
  "failed"
];
var END_REASONS = [
  "consumer_hangup",
  "companion_hangup",
  "insufficient_balance",
  "heartbeat_lost",
  "media_failed",
  "admin_force_end",
  "callee_no_answer",
  "callee_rejected",
  "callee_busy",
  "recovered_orphan"
];
var BOOKING_TYPES = ["chat", "voice", "video", "in_person"];
var PRESENCE_STATES = ["offline", "online", "busy", "away"];
var LEDGER_TXN_KINDS = [
  "recharge",
  "bonus_grant",
  "bonus_expiry",
  "session_authorize",
  "session_settle",
  "session_refund",
  "companion_accrual",
  "payout",
  "payout_reversal",
  "refund",
  "chargeback",
  "clawback",
  "admin_adjustment"
];
var REPORT_REASONS = [
  "harassment",
  "nudity",
  "abuse",
  "spam",
  "impersonation",
  "off_platform_solicitation",
  "underage",
  "fraud",
  "other"
];

// src/env.ts
function loadEnv(schema, source) {
  const result = schema.safeParse(source);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `  \u2022 ${i.path.join(".") || "(root)"}: ${i.message}`).join("\n");
    throw new Error(
      `Invalid environment configuration:
${issues}

Check your .env against .env.example.`
    );
  }
  return result.data;
}
function redact(value) {
  if (!value) return "(unset)";
  if (value.length <= 8) return "****";
  return `${value.slice(0, 4)}\u2026${value.slice(-4)}`;
}

// src/chat.ts
var conversationRoom = (conversationId) => `conversation:${conversationId}`;
var userRoom = (userId) => `user:${userId}`;
var MESSAGE_MAX_LENGTH = 4e3;
var dependencyStatusSchema = z.object({
  name: z.string(),
  status: z.enum(["up", "down", "not_configured"]),
  detail: z.string().optional(),
  latencyMs: z.number().optional()
});
var healthResponseSchema = z.object({
  status: z.enum(["ok", "degraded", "down"]),
  service: z.string(),
  version: z.string(),
  phase: z.string(),
  uptimeSec: z.number(),
  checkedAt: z.string(),
  dependencies: z.array(dependencyStatusSchema)
});

// src/redis-keys.ts
var redisKeys = {
  /** Worker liveness. The API's /health reads this. TTL'd, so a dead worker disappears. */
  workerHeartbeat: "worker:heartbeat",
  /** Phase 3 — presence, TTL 45s. Expiry IS the offline signal. */
  presence: (userId) => `presence:${userId}`,
  onlineCompanions: "presence:online:companions",
  /** Phase 5 — hot billing state for a live session. */
  session: (sessionId) => `session:${sessionId}`,
  /** Phase 2 — short-lived lock around a wallet mutation. */
  walletLock: (userId) => `lock:wallet:${userId}`
};
var REDIS_TTL = {
  presenceSec: 45,
  workerHeartbeatSec: 90,
  sessionSec: 6 * 60 * 60
};

// src/settings.ts
var SETTING_KEYS = {
  userPlatformFeePaise: "fee.user_platform_paise",
  companionPlatformFeePaise: "fee.companion_platform_paise",
  companionPaysBeforeApproval: "fee.companion_pays_before_approval",
  withdrawalMinimumPaise: "withdrawal.minimum_paise",
  withdrawalHoldSeconds: "withdrawal.dispute_hold_seconds",
  companionSharePct: "revenue.companion_share_pct",
  coinValuePaise: "revenue.coin_value_paise",
  cancellationFreeWindowSeconds: "cancellation.free_window_seconds",
  cancellationLateRefundPct: "cancellation.late_refund_pct",
  noShowRefundPct: "cancellation.no_show_refund_pct",
  bookingRequestExpirySeconds: "booking.request_expiry_seconds",
  bookingMinDurationMinutes: "booking.min_duration_minutes",
  walletMinTopupPaise: "wallet.min_topup_paise",
  walletLowBalanceCoins: "wallet.low_balance_coins"
};
var SETTING_FALLBACKS = {
  "fee.user_platform_paise": 19900,
  "fee.companion_platform_paise": 0,
  "fee.companion_pays_before_approval": false,
  "withdrawal.minimum_paise": 1e5,
  "withdrawal.dispute_hold_seconds": 259200,
  "revenue.companion_share_pct": 60,
  "revenue.coin_value_paise": 30,
  "cancellation.free_window_seconds": 3600,
  "cancellation.late_refund_pct": 50,
  "cancellation.no_show_refund_pct": 0,
  "booking.request_expiry_seconds": 900,
  "booking.min_duration_minutes": 60,
  "wallet.min_topup_paise": 1e4,
  "wallet.low_balance_coins": 50
};
var paiseToRupees = (paise) => `\u20B9${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
function humanDuration(seconds) {
  if (seconds === 0) return "none";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor(seconds % 86400 / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (hours) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  if (minutes) parts.push(`${minutes} min`);
  return parts.join(" ") || `${seconds}s`;
}

export { BOOKING_TYPES, COMPANION_STATUSES, END_REASONS, KYC_STATUSES, LEDGER_TXN_KINDS, MESSAGE_MAX_LENGTH, PRESENCE_STATES, REDIS_TTL, REPORT_REASONS, SESSION_STATUSES, SESSION_TYPES, SETTING_FALLBACKS, SETTING_KEYS, USER_ROLES, conversationRoom, dependencyStatusSchema, healthResponseSchema, humanDuration, loadEnv, paiseToRupees, redact, redisKeys, userRoom };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map